/**
 * Derives primary / subtitle copy, elapsed time, and idle-based escalation
 * while the copilot stream is active. Used by ChatPanel (indicator + composer).
 */

import { useEffect, useState } from 'react';
import { useChatStore } from '../../../chat/core/ChatStoreContext';

export interface CopilotStreamingStatus {
  isActive: boolean;
  primary: string;
  staleSubtitle: string | null;
  elapsedSec: number;
  idleSec: number;
  toolCallCount: number;
}

export interface StreamingStatusFields {
  isStreaming: boolean;
  streamingAction: string;
  streamingContent: string;
  streamingToolCallCount: number;
  streamingStartedAt: number | null;
  streamingLastEventAt: number | null;
}

export function deriveStreamingStatus(fields: StreamingStatusFields): CopilotStreamingStatus {
  const {
    isStreaming,
    streamingAction,
    streamingContent,
    streamingToolCallCount,
    streamingStartedAt,
    streamingLastEventAt,
  } = fields;

  if (!isStreaming || streamingStartedAt === null || streamingLastEventAt === null) {
    return {
      isActive: false,
      primary: '',
      staleSubtitle: null,
      elapsedSec: 0,
      idleSec: 0,
      toolCallCount: 0,
    };
  }

  const now = Date.now();
  const elapsedSec = Math.max(0, Math.floor((now - streamingStartedAt) / 1000));
  const idleSec = Math.max(0, Math.floor((now - streamingLastEventAt) / 1000));

  const hasContent = Boolean(streamingContent.trim());
  const rawAction = streamingAction.trim();
  const nToolCalls = streamingToolCallCount;

  const isGenericThinking = rawAction === 'Thinking…' && !hasContent && nToolCalls === 0;

  let primary: string;
  if (rawAction) {
    if (isGenericThinking) {
      if (idleSec >= 10) primary = 'Still working on your question…';
      else if (idleSec >= 4) primary = 'Choosing next steps…';
      else primary = 'Thinking…';
    } else {
      primary = rawAction;
    }
  } else if (hasContent) {
    primary = 'Composing answer…';
  } else if (nToolCalls > 0) {
    primary = 'Working…';
  } else {
    if (idleSec >= 10) primary = 'Still working on your question…';
    else if (idleSec >= 4) primary = 'Choosing next steps…';
    else primary = 'Thinking…';
  }

  const thinkingEscalated =
    primary === 'Choosing next steps…' || primary === 'Still working on your question…';

  let staleSubtitle: string | null = null;
  if (idleSec >= 5 && !thinkingEscalated) {
    if (rawAction && !isGenericThinking) {
      staleSubtitle =
        idleSec >= 12
          ? 'Large queries and multi-step reasoning can take a little while.'
          : 'This step is taking longer than usual…';
    } else if (hasContent) {
      if (idleSec >= 10) staleSubtitle = 'Still composing…';
    } else if (nToolCalls > 0 && !hasContent) {
      staleSubtitle =
        idleSec >= 12
          ? 'Almost done with this stretch of work…'
          : 'Finishing the current step…';
    }
  }

  return {
    isActive: true,
    primary,
    staleSubtitle,
    elapsedSec,
    idleSec,
    toolCallCount: nToolCalls,
  };
}

export function useStreamingStatus(fields: StreamingStatusFields): CopilotStreamingStatus {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!fields.isStreaming) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [fields.isStreaming]);

  return deriveStreamingStatus(fields);
}

/** Status of the chat being shown — a docked chat's, or the Copilot page's. */
export function useCopilotStreamingStatus(): CopilotStreamingStatus {
  const isStreaming = useChatStore((s) => s.isStreaming);
  const streamingAction = useChatStore((s) => s.streamingAction);
  const streamingContent = useChatStore((s) => s.streamingContent);
  const streamingToolCallCount = useChatStore((s) => s.streamingToolCallCount);
  const streamingStartedAt = useChatStore((s) => s.streamingStartedAt);
  const streamingLastEventAt = useChatStore((s) => s.streamingLastEventAt);

  return useStreamingStatus({
    isStreaming,
    streamingAction,
    streamingContent,
    streamingToolCallCount,
    streamingStartedAt,
    streamingLastEventAt,
  });
}
